import React from "react";
import classNames from "classnames";

import styles from "./PromoCarousel.css";
import { Game } from "../../models/Game";
import { PromoTile } from "../../models/PromoTile";
import { GridRow } from "../../atoms/Layout/GridRow/GridRow";
import { PromoArrowIcon } from "../../atoms/Icons/PromoArrowIcon";
import { Analytics } from "../../services/Analytics/Analytics";
import { CarouselTile } from "../Tiles/CarouselTile";

type PromoCarouselProps = {
  games: Game[];
  promoEvents: PromoEvent[];
};

export type PromoEvent = {
  eventKey: string;
  eventName: string;
  eventDateStart: string;
  eventDateEnd: string;
  promo: (PromoItem | string)[];
};

type PromoCarouselState = {
  activeIndex: number;
  promoTiles: PromoTile[];
  stopAnimation: boolean;
};

export type PromoItem = {
  key: string;
  title: string;
  description: string;
  buttonTitle: string;
  link: string;
  imageSrc: string;
  isTargetBlank: boolean;
  alt?: string;
};

const DEBOUNCE = 250;

export class PromoCarousel extends React.PureComponent<
  PromoCarouselProps,
  PromoCarouselState
> {
  state = {
    activeIndex: 0,
    promoTiles: this.getCurrentPromo(),
    stopAnimation: false,
  };

  timerId: any = -1;

  waitTimer: any = -1;
  moveTimer: any = -1;

  getCurrentPromo() {
    return this.getEventByDate(this.props.promoEvents);
  }
  getEventByDate(allEvents: PromoEvent[]): any {
    const today = new Date().getTime();
    const dateRangesTodayBelongsTo = allEvents.filter((dateRange) => {
      const firstEventDate = new Date(dateRange.eventDateStart).setHours(
        0,
        0,
        0
      );
      const lastEventDate = new Date(dateRange.eventDateEnd).setHours(
        23,
        59,
        59
      );
      return firstEventDate <= today && lastEventDate >= today;
    });
    if (!dateRangesTodayBelongsTo.length) {
      return this.getPromoTiles(["aarp-trivia"]);
    } else {
      let clonedEvent = JSON.parse(JSON.stringify(dateRangesTodayBelongsTo[0]));
      return this.getPromoTiles(clonedEvent.promos);
    }
  }

  getPromoTiles(promo: any): PromoTile[] {
    return promo
      .map((item: PromoItem | string) => {
        if (typeof item === "string") {
          const game = this.props.games.find(
            (g) => g.slug === item && !!g
          ) as Game;
          return game ? PromoTile.fromGame(game) : null;
        }
        return PromoTile.fromData(item);
      })
      .filter((promo) => !!promo);
  }

  moveLeft = () => {
    this.startTimer();
    clearTimeout(this.moveTimer);
    clearTimeout(this.waitTimer);

    this.moveTimer = setTimeout(() => {
      const { promoTiles } = this.state;
      const { activeIndex } = this.state;

      if (activeIndex - 1 < 0) {
        promoTiles.unshift(promoTiles[promoTiles.length - 1]);
        promoTiles.pop();
        this.setState(
          {
            promoTiles,
            activeIndex: 1,
            stopAnimation: true,
          },
          () => {
            this.waitTimer = setTimeout(() => {
              this.setState({
                stopAnimation: false,
                activeIndex: 0,
              });
            }, DEBOUNCE);
          }
        );
        return;
      }

      this.setState({
        activeIndex: Math.max(this.state.activeIndex - 1, 0),
        stopAnimation: false,
      });
    }, DEBOUNCE);
  };

  moveRight = () => {
    this.startTimer();
    clearTimeout(this.moveTimer);
    clearTimeout(this.waitTimer);

    this.moveTimer = setTimeout(() => {
      let { promoTiles } = this.state;
      const { activeIndex } = this.state;

      if (activeIndex + 1 > promoTiles.length - 1) {
        promoTiles.push(promoTiles[0]);
        promoTiles = promoTiles.slice(1);
        this.setState(
          {
            promoTiles,
            activeIndex: Math.max(activeIndex - 1, 0),
            stopAnimation: true,
          },
          () => {
            this.waitTimer = setTimeout(() => {
              this.setState({
                stopAnimation: false,
                activeIndex,
              });
            }, DEBOUNCE);
          }
        );
        return;
      }

      this.setState({
        activeIndex: activeIndex + 1,
        stopAnimation: false,
      });
    }, DEBOUNCE);
  };

  startTimer = () => {
    clearInterval(this.timerId);
    this.timerId = setInterval(this.moveRight, 6000);
  };

  stopTimer = () => {
    clearInterval(this.timerId);
    clearInterval(this.waitTimer);
    clearInterval(this.moveTimer);
  };

  componentDidMount() {
    this.startTimer();
    const { promoTiles } = this.state;
    const promoKey = promoTiles.map((item) => item.key).join("|");
    Analytics.trackEvent(Analytics.home.promoImpression(promoKey));
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  trackClick(promoTile: PromoTile, index: number) {
    Analytics.trackEvent(Analytics.home.promoClick(promoTile.key, index));
  }

  render() {
    return (
      <Container
        data-element-description="promo carousel"
        onMouseMove={this.stopTimer}
        onMouseLeave={this.startTimer}
      >
        <MoveButton
          aria-label="promo previous game"
          data-element-description="promo carousel move left button"
          onClick={this.moveLeft}
          active={true}
        >
          <LeftButtonIcon />
        </MoveButton>

        <CarouselList>
          <List
            style={{
              transform: `translateX(-${this.state.activeIndex * 100}%)`,
            }}
            hasAnimation={!this.state.stopAnimation}
          >
            {this.state.promoTiles.map((item, ind) => (
              <CarouselItem
                key={item.key}
                onClick={() => this.trackClick(item, ind)}
                promoTile={item}
                elementDescription={`promo tile ${
                  this.state.activeIndex === ind ? "active" : ""
                }`}
              />
            ))}
          </List>
        </CarouselList>

        <MoveButton
          aria-label="promo next game"
          data-element-description="promo carousel move right button"
          onClick={this.moveRight}
          active={true}
        >
          <RightButtonIcon />
        </MoveButton>
      </Container>
    );
  }
}

const Container = (props: any) => (
  <div className={styles.container} {...props} />
);

const CarouselList = (props: any) => (
  <div className={styles.gamesList} {...props} />
);

const CarouselItem = (props: any) => (
  <CarouselTile className={classNames(styles.gamesListItem)} {...props} />
);

const MoveButton = ({ active, ...props }: any) => (
  <button
    className={classNames(styles.moveButton, { [styles.active]: !!active })}
    {...props}
  />
);

const List = ({ hasAnimation, ...props }: any) => (
  <GridRow
    style={props.style}
    className={classNames(styles.list, {
      [styles.hasAnimation]: hasAnimation,
    })}
    {...props}
  />
);

const RightButtonIcon = (props: any) => (
  <PromoArrowIcon className={styles.rightButtonIcon} {...props} />
);
const LeftButtonIcon = (props: any) => (
  <PromoArrowIcon className={styles.leftButtonIcon} {...props} />
);
